import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useDataRefresh, formatLastUpdated } from '@/hooks/useDataRefresh';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  Search,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Package,
  Filter,
  Download,
  Globe,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Layers
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'live', label: 'Live', icon: CheckCircle2, color: 'text-green-600 bg-green-50 border-green-200' },
  { value: 'not_live', label: 'Not Live', icon: XCircle, color: 'text-red-600 bg-red-50 border-red-200' },
];

const PRODUCT_TYPE_OPTIONS = [
  { value: 'Single', label: 'Single', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'Combo', label: 'Combo', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'Multi-Product', label: 'Multi-Product', color: 'bg-purple-100 text-purple-700 border-purple-200' },
];

const getProductTypeConfig = (type) => {
  return PRODUCT_TYPE_OPTIONS.find(t => t.value === type) || PRODUCT_TYPE_OPTIONS[0];
};

const PAGE_SIZE_OPTIONS = [10, 15, 20, 25, 50];

const getStatusConfig = (status) => {
  return STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[1];
};

export default function SKULiveStatusPage() {
  const { } = useAuth();
  const [products, setProducts] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [platformIcons, setPlatformIcons] = useState({});
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [search, setSearch] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [productTypeFilter, setProductTypeFilter] = useState('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [productsRes, platformsRes, iconsRes, brandsRes] = await Promise.all([
        fetch(`${API_URL}/api/masters/skus`, { credentials: 'include' }),
        fetch(`${API_URL}/api/platforms`, { credentials: 'include' }),
        fetch(`${API_URL}/api/platform-icons`, { credentials: 'include' }),
        fetch(`${API_URL}/api/masters/brands`, { credentials: 'include' })
      ]);

      if (productsRes.ok) setProducts(await productsRes.json());
      if (platformsRes.ok) {
        const platformData = await platformsRes.json();
        // Filter out Meta - it's only for Task Submission
        const filteredPlatforms = platformData
          .map(p => typeof p === 'string' ? p : p.name)
          .filter(p => p !== 'Meta');
        setPlatforms(filteredPlatforms);
      }
      if (iconsRes.ok) setPlatformIcons(await iconsRes.json());
      if (brandsRes.ok) setBrands(await brandsRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh on window focus
  const { refresh, lastUpdated } = useDataRefresh(fetchData, {
    refreshOnFocus: true,
    refreshInterval: 120000, // 2 minutes
    minRefreshGap: 10000
  });

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Status is determined purely by URL presence - no manual override
  // Status is determined purely by URL presence - no manual override
  const getProductStatus = (productId, platform, platformUrls) => {
    const hasUrl = platformUrls && platformUrls[platform] && platformUrls[platform].trim();
    return hasUrl ? 'live' : 'not_live';
  };

  const getBrandName = (brandId) => {
    const brand = brands.find(b => b.id === brandId);
    return brand?.name || 'Unknown';
  };

  const getBrandIcon = (brandId) => {
    const brand = brands.find(b => b.id === brandId);
    return brand?.icon_url || null;
  };

  // Filter products
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesSearch = !search || 
        product.product_name.toLowerCase().includes(search.toLowerCase());
      const matchesBrand = selectedBrand === 'all' || product.brand_id === selectedBrand;
      const matchesProductType = productTypeFilter === 'all' || 
        (product.product_type || 'Single') === productTypeFilter;
      
      // Status filter - check if any platform matches the status
      let matchesStatus = statusFilter === 'all';
      if (!matchesStatus) {
        const platformsToCheck = selectedPlatform === 'all' ? platforms : [selectedPlatform];
        matchesStatus = platformsToCheck.some(platform => {
          const status = getProductStatus(product.id, platform, product.platform_urls);
          return status === statusFilter;
        });
      }
      
      return matchesSearch && matchesBrand && matchesStatus && matchesProductType;
    });
  }, [products, search, selectedBrand, statusFilter, selectedPlatform, platforms, productTypeFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / pageSize);
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredProducts.slice(start, start + pageSize);
  }, [filteredProducts, currentPage, pageSize]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedBrand, selectedPlatform, statusFilter, pageSize, productTypeFilter]);

  // Get platforms to display in table
  const displayPlatforms = useMemo(() => {
    if (selectedPlatform === 'all') return platforms;
    return [selectedPlatform];
  }, [selectedPlatform, platforms]);

  // Stats
  const stats = useMemo(() => {
    let live = 0, notLive = 0;
    
    filteredProducts.forEach(product => {
      displayPlatforms.forEach(platform => {
        const status = getProductStatus(product.id, platform, product.platform_urls);
        if (status === 'live') live++;
        else if (status === 'not_live') notLive++;
      });
    });
    
    // Total = unique products count, not product-platform combinations
    return { live, notLive, totalProducts: filteredProducts.length };
  }, [filteredProducts, displayPlatforms]);

  const resetFilters = () => {
    setSearch('');
    setSelectedPlatform('all');
    setSelectedBrand('all');
    setStatusFilter('all');
    setProductTypeFilter('all');
  };

  const hasActiveFilters = search || selectedPlatform !== 'all' || selectedBrand !== 'all' || statusFilter !== 'all' || productTypeFilter !== 'all';

  const exportData = () => {
    const rows = [];
    rows.push(['Product', 'Brand', ...displayPlatforms].join(','));
    
    filteredProducts.forEach(product => {
      const row = [
        `"${product.product_name}"`,
        `"${getBrandName(product.brand_id)}"`,
        ...displayPlatforms.map(platform => {
          const status = getProductStatus(product.id, platform, product.platform_urls);
          return getStatusConfig(status).label;
        })
      ];
      rows.push(row.join(','));
    });
    
    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `sku_live_status_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success('Exported successfully');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-[#2EB4AC]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sku-live-status-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">SKUs Live Status</h1>
          <p className="text-slate-500 mt-1">Track product availability across platforms</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-slate-400">
              Updated {formatLastUpdated(lastUpdated)}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            className="text-slate-600"
            data-testid="refresh-sku-status-btn"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={exportData}
            className="border-slate-300"
            data-testid="export-status-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Platform Selector - Horizontal Pills */}
      <Card className="bg-white border border-slate-200">
        <CardContent className="py-4">
          {/* Platform Row */}
          <div className="mb-4">
            <p className="text-xs font-medium text-slate-500 mb-2">Platform</p>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setSelectedPlatform('all')}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-all text-sm font-medium",
                  selectedPlatform === 'all'
                    ? "border-[#2EB4AC] bg-[#2EB4AC]/5 text-[#2EB4AC]"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                )}
              >
                <Globe className="h-4 w-4" />
                All Platforms
              </button>
              {platforms.map(platform => (
                <button
                  key={platform}
                  onClick={() => setSelectedPlatform(platform)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-all text-sm font-medium",
                    selectedPlatform === platform
                      ? "border-[#2EB4AC] bg-[#2EB4AC]/5 text-[#2EB4AC]"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  )}
                >
                  {platformIcons[platform] ? (
                    <img src={platformIcons[platform]} alt={platform} className="w-4 h-4 object-contain" />
                  ) : (
                    <span className="w-4 h-4 rounded bg-slate-200 flex items-center justify-center text-[9px] font-bold">
                      {platform.charAt(0)}
                    </span>
                  )}
                  {platform}
                </button>
              ))}
            </div>
          </div>

          {/* Brand Row */}
          <div className="mb-4">
            <p className="text-xs font-medium text-slate-500 mb-2">Brand</p>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setSelectedBrand('all')}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-all text-sm font-medium",
                  selectedBrand === 'all'
                    ? "border-[#2EB4AC] bg-[#2EB4AC]/5 text-[#2EB4AC]"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                )}
              >
                All Brands
              </button>
              {brands.map(brand => (
                <button
                  key={brand.id}
                  onClick={() => setSelectedBrand(brand.id)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-all text-sm font-medium",
                    selectedBrand === brand.id
                      ? "border-[#2EB4AC] bg-[#2EB4AC]/5 text-[#2EB4AC]"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  )}
                >
                  {brand.icon_url ? (
                    <img src={brand.icon_url} alt={brand.name} className="w-4 h-4 object-contain rounded" />
                  ) : (
                    <span className="w-4 h-4 rounded bg-slate-200 flex items-center justify-center text-[9px] font-bold text-slate-500">
                      {brand.name.charAt(0)}
                    </span>
                  )}
                  {brand.name}
                </button>
              ))}
            </div>
          </div>

          {/* Product Type Row */}
          <div>
            <p className="text-xs font-medium text-slate-500 mb-2">Product Type</p>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setProductTypeFilter('all')}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-all text-sm font-medium",
                  productTypeFilter === 'all'
                    ? "border-[#2EB4AC] bg-[#2EB4AC]/5 text-[#2EB4AC]"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                )}
              >
                <Layers className="h-4 w-4" />
                All Types
              </button>
              {PRODUCT_TYPE_OPTIONS.map(type => (
                <button
                  key={type.value}
                  onClick={() => setProductTypeFilter(type.value)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-all text-sm font-medium",
                    productTypeFilter === type.value
                      ? "border-[#2EB4AC] bg-[#2EB4AC]/5 text-[#2EB4AC]"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  )}
                >
                  <span className={cn(
                    "w-2 h-2 rounded-full",
                    type.value === 'Single' ? "bg-blue-500" :
                    type.value === 'Combo' ? "bg-amber-500" : "bg-purple-500"
                  )} />
                  {type.label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters Section */}
      <Card className="bg-white border border-slate-200">
        <CardContent className="py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-500" />
              <span className="font-medium text-slate-700">Filters</span>
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={resetFilters} className="text-slate-500 hover:text-slate-700">
                <RefreshCw className="h-4 w-4 mr-1" />
                Reset
              </Button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-slate-500 mb-1.5">Status</p>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {STATUS_OPTIONS.map(status => (
                    <SelectItem key={status.value} value={status.value}>
                      <div className="flex items-center gap-2">
                        <status.icon className="h-4 w-4" />
                        {status.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <p className="text-xs text-slate-500 mb-1.5">Search</p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search products..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 h-10"
                  data-testid="search-input"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card 
          className="bg-white border border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setStatusFilter('all')}
        >
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Total Products</p>
                <p className="text-2xl font-bold text-slate-900">{stats.totalProducts}</p>
              </div>
              <Package className="h-8 w-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className={cn(
            "border cursor-pointer hover:shadow-md transition-shadow",
            statusFilter === 'live' ? "bg-green-100 border-green-400 ring-2 ring-green-400" : "bg-green-50 border-green-200"
          )}
          onClick={() => setStatusFilter(statusFilter === 'live' ? 'all' : 'live')}
        >
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600">Live Listings</p>
                <p className="text-2xl font-bold text-green-700">{stats.live}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className={cn(
            "border cursor-pointer hover:shadow-md transition-shadow",
            statusFilter === 'not_live' ? "bg-red-100 border-red-400 ring-2 ring-red-400" : "bg-red-50 border-red-200"
          )}
          onClick={() => setStatusFilter(statusFilter === 'not_live' ? 'all' : 'not_live')}
        >
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-600">Not Live</p>
                <p className="text-2xl font-bold text-red-700">{stats.notLive}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Matrix Table */}
      <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Globe className="h-5 w-5 text-[#2EB4AC]" />
                Platform Status Matrix
              </CardTitle>
              <CardDescription>
                {filteredProducts.length} products • {displayPlatforms.length} platform{displayPlatforms.length > 1 ? 's' : ''}
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={fetchData} className="text-slate-500">
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="w-full">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {displayPlatforms.length === 1 ? (
                    <>
                      <TableHead className="font-semibold w-[30%]">Product</TableHead>
                      <TableHead className="font-semibold w-[15%]">Brand</TableHead>
                      <TableHead className="font-semibold w-[20%] text-center">Status</TableHead>
                      <TableHead className="font-semibold w-[35%]">Platform URL</TableHead>
                    </>
                  ) : (
                    <>
                      <TableHead className="font-semibold sticky left-0 bg-slate-50 z-10 min-w-[200px]">
                        Product
                      </TableHead>
                      {displayPlatforms.map(platform => (
                        <TableHead key={platform} className="text-center min-w-[60px]">
                          <div className="flex flex-col items-center gap-0.5">
                            {platformIcons[platform] ? (
                              <img src={platformIcons[platform]} alt={platform} className="w-6 h-6 object-contain" />
                            ) : (
                              <Globe className="h-5 w-5 text-slate-400" />
                            )}
                            <span className="text-[10px]">{platform}</span>
                          </div>
                        </TableHead>
                      ))}
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedProducts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={displayPlatforms.length === 1 ? 4 : displayPlatforms.length + 1} className="text-center py-12 text-slate-500">
                      <Package className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p>No products found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedProducts.map(product => {
                    // Single platform view - cleaner row layout
                    if (displayPlatforms.length === 1) {
                      const platform = displayPlatforms[0];
                      const status = getProductStatus(product.id, platform, product.platform_urls);
                      const statusConfig = getStatusConfig(status);
                      const StatusIcon = statusConfig.icon;
                      const hasUrl = product.platform_urls && product.platform_urls[platform];

                      return (
                        <TableRow key={product.id} className="hover:bg-slate-50">
                          <TableCell className="font-medium py-3">
                            <div className="flex items-center gap-3">
                              {product.image_url ? (
                                <img 
                                  src={product.image_url} 
                                  alt={product.product_name} 
                                  className="w-10 h-10 rounded object-cover border border-slate-200"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded bg-slate-100 border border-slate-200 flex items-center justify-center">
                                  <Package className="h-5 w-5 text-slate-400" />
                                </div>
                              )}
                              <div className="flex flex-col gap-1">
                                <span className="text-slate-900">{product.product_name}</span>
                                {(product.product_type && product.product_type !== 'Single') && (
                                  <Badge 
                                    variant="secondary" 
                                    className={cn(
                                      "text-[10px] px-1.5 py-0 h-4 w-fit",
                                      getProductTypeConfig(product.product_type).color
                                    )}
                                  >
                                    {product.product_type}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-600 text-sm py-3">
                            <div className="flex items-center gap-2">
                              {getBrandIcon(product.brand_id) && (
                                <img src={getBrandIcon(product.brand_id)} alt="" className="w-5 h-5 rounded object-contain" />
                              )}
                              <span>{getBrandName(product.brand_id)}</span>
                            </div>
                          </TableCell>
                          <TableCell className="py-3">
                            <div className="flex justify-center">
                              <div 
                                className={cn(
                                  "flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-medium",
                                  statusConfig.color
                                )}
                              >
                                <StatusIcon className="h-4 w-4" />
                                <span>{statusConfig.label}</span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="py-3">
                            {hasUrl ? (
                              <a
                                href={product.platform_urls[platform]}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#2EB4AC] hover:underline flex items-center gap-2 group"
                              >
                                <ExternalLink className="h-4 w-4 flex-shrink-0" />
                                <span className="truncate max-w-[280px] group-hover:text-[#25908A]">
                                  {product.platform_urls[platform]}
                                </span>
                              </a>
                            ) : (
                              <span className="text-slate-400 text-sm italic">No URL configured</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    }

                    // Multi-platform view - compact icons
                    return (
                      <TableRow key={product.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium sticky left-0 bg-white z-10 py-2">
                          <div className="flex items-center gap-2">
                            {product.image_url ? (
                              <img 
                                src={product.image_url} 
                                alt={product.product_name} 
                                className="w-8 h-8 rounded object-cover border border-slate-200 flex-shrink-0"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0">
                                <Package className="h-4 w-4 text-slate-400" />
                              </div>
                            )}
                            <div className="flex flex-col gap-0.5">
                              <span className="text-slate-900 text-sm">{product.product_name}</span>
                              {(product.product_type && product.product_type !== 'Single') && (
                                <Badge 
                                  variant="secondary" 
                                  className={cn(
                                    "text-[9px] px-1 py-0 h-3.5 w-fit",
                                    getProductTypeConfig(product.product_type).color
                                  )}
                                >
                                  {product.product_type}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        {displayPlatforms.map(platform => {
                          const status = getProductStatus(product.id, platform, product.platform_urls);
                          const statusConfig = getStatusConfig(status);
                          const StatusIcon = statusConfig.icon;
                          const hasUrl = product.platform_urls && product.platform_urls[platform];
                          const isCompact = displayPlatforms.length > 3;

                          return (
                            <TableCell key={platform} className={cn("text-center", isCompact ? "p-1" : "p-2")}>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="flex justify-center">
                                      <div 
                                        className={cn(
                                          "border rounded-lg flex items-center justify-center cursor-default",
                                          statusConfig.color,
                                          isCompact 
                                            ? "w-10 h-10" 
                                            : "w-[130px] h-9 gap-2 text-sm px-3"
                                        )}
                                        data-testid={`status-${product.id}-${platform}`}
                                      >
                                        <StatusIcon className={cn(isCompact ? "h-5 w-5" : "h-4 w-4")} />
                                        {!isCompact && <span>{statusConfig.label}</span>}
                                      </div>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <div className="text-xs space-y-1">
                                      <p className="font-medium">{statusConfig.label}</p>
                                      {hasUrl && (
                                        <a 
                                          href={product.platform_urls[platform]} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="text-[#2EB4AC] hover:underline flex items-center gap-1"
                                        >
                                          View URL <ExternalLink className="h-3 w-3" />
                                        </a>
                                      )}
                                      {!hasUrl && <p className="text-slate-400">No URL configured</p>}
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages >= 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
              <div className="flex items-center gap-4">
                <p className="text-sm text-slate-500">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, filteredProducts.length)} of {filteredProducts.length} products
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Show:</span>
                  <Select value={pageSize.toString()} onValueChange={(v) => setPageSize(parseInt(v))}>
                    <SelectTrigger className="w-[80px] h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map(size => (
                        <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="h-8"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                          className={cn(
                            "h-8 w-8 p-0",
                            currentPage === pageNum && "bg-[#2EB4AC] hover:bg-[#25908A]"
                          )}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="h-8"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 text-sm text-slate-500">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          <span>Live - Product URL is configured</span>
        </div>
        <div className="flex items-center gap-2">
          <XCircle className="h-4 w-4 text-red-500" />
          <span>Not Live - No URL configured</span>
        </div>
      </div>
    </div>
  );
}
